import { z } from "zod";
import { APPROVAL_STATUSES } from "@/features/approvals/approval-vocabulary";

/// URL search-params schema for `/$slug/approvals` (FND-026). The `scope`
/// param is the single tab dimension on the unified approvals inbox:
///
///   for-me    , requests waiting on me to decide (default; everyone who can
///                reach the route holds APPROVAL_VIEW_OWN)
///   from-me   , requests I raised, whatever their current status (everyone)
///   workspace , every request in the workspace, bypasses "waiting on me"
///                (admins only, via APPROVAL_VIEW_WORKSPACE)
///
/// Wire values are hyphenated to match the shore's own
/// `APPROVAL_INBOX_SCOPES` (`approvals-inbox.dto.ts`), so there is no second
/// spelling to keep in sync.
///
/// Mirrors `src/features/feedback/routes/feedback.route.search.ts`'s shape:
/// `.catch(...)` normalizes an unrecognized value to the default INSIDE the
/// schema (never throws). The route body still redirects a caller who lacks
/// APPROVAL_VIEW_WORKSPACE off of `workspace` (defence in depth, a bookmarked
/// or hand-typed URL can't bypass the gate); `resolveApprovalScope` is the
/// pure half of that redirect, split out here so it is testable without
/// rendering hooks (this repo has no jsdom / testing-library).
export const APPROVAL_SCOPES = ["for-me", "from-me", "workspace"] as const;
export type ApprovalScope = (typeof APPROVAL_SCOPES)[number];

/// `status` filter values. Deliberately a SEPARATE enum from the domain
/// `ApprovalStatus` (mirrors `feedbackStatusFilterSchema` in
/// `feedback.schema.ts`, a filter-only enum distinct from feedback's own
/// domain shape): `"all"` is a filter concept, not a request state a row
/// can actually be in, and it means something different from every other
/// value here. "pending" is the page's default lens; the three OTHER real
/// statuses are narrower alternatives to it; "all" WIDENS past the default
/// rather than narrowing it (see `from-me`'s own definition above: "whatever
/// their current status"). `buildApprovalsListFilters`
/// (`lib/build-approvals-list-filters.ts`) is where `"all"` turns into the
/// `undefined` the shore's list endpoint reads as "every status", the one
/// spot that mapping happens.
export const APPROVAL_STATUS_FILTERS = [...APPROVAL_STATUSES, "all"] as const;
export type ApprovalStatusFilter = (typeof APPROVAL_STATUS_FILTERS)[number];

export const approvalsRouteSearchSchema = z
  .object({
    scope: z.enum(APPROVAL_SCOPES).catch("for-me").default("for-me"),
    /// Defaults to "pending", not "all": the shore's counts badge
    /// (`countPendingForScope`) is hardcoded to pending-only, so an
    /// unfiltered list request that came back with every status would show
    /// "3 pending" above 47 rows on first paint. Defaulting the URL itself
    /// to "pending" means every code path, including a caller who lands
    /// here with no query string at all, carries the explicit value rather
    /// than relying on a caller to remember to set it.
    status: z.enum(APPROVAL_STATUS_FILTERS).catch("pending").default("pending"),
    /// No default: unset means "don't filter by request type". Options are
    /// derived from loaded rows (`lib/approval-subject-type-options.ts`),
    /// never a hardcoded list, since no production domain has registered a
    /// `subjectType` with the shore's `ApprovalRegistry` yet.
    subjectType: z.string().optional(),
    /// Set only by the shore's EMAIL notification template
    /// (`hr-approvals.notifications.bootstrapper.ts`, `deepLink:
    /// /approvals?focus=<approvalRequestId>`), never by in-app navigation:
    /// every other entry point to this route omits it, so it is optional.
    /// It must be declared here, not ignored: this object carries
    /// `.strict()`, and `.strict()` has no per-field `.catch()` to fall
    /// back on. An unrecognized `focus` key fails the WHOLE parse (the
    /// branch's own `safeParse({ bogus: "x" }).success === false` test
    /// above proves it), which TanStack Router would surface as an
    /// uncaught `SearchParamError` (no `errorComponent` is registered in
    /// `router.tsx`). Deleting this field as "unused" would reintroduce
    /// that failure the moment a `focus` key reaches this route.
    ///
    /// That said, no `focus` key reaches this route today. The shore emits
    /// `${APP_URL}/approvals?focus=<id>` with no `/$workspaceSlug` segment
    /// (`hr-approvals.notifications.bootstrapper.ts:53,61,72`,
    /// `notification-dispatch.service.ts:245`), so on this router
    /// `approvals` itself matches `$workspaceSlug` and the URL resolves to
    /// the workspace HOME route instead, whose search schema is a separate,
    /// non-strict `z.object` that silently drops `focus`. The recipient
    /// lands on some workspace home, not the approvals inbox and not an
    /// error screen. This field is correct and ready for the day the
    /// shore's link carries a workspace slug; until then it is declared but
    /// never exercised in production. That is shore-side work this repo
    /// cannot do.
    focus: z.string().optional(),
  })
  .strict();
export type ApprovalsRouteSearch = z.infer<typeof approvalsRouteSearchSchema>;

/// The redirect target for a scope the caller cannot use. `workspace`
/// requires `canViewWorkspace` (APPROVAL_VIEW_WORKSPACE); every other scope
/// is open to anyone who can reach this route at all.
export function resolveApprovalScope(
  scope: ApprovalScope,
  canViewWorkspace: boolean,
): ApprovalScope {
  return scope !== "workspace" || canViewWorkspace ? scope : "for-me";
}
